import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import useRole from "../hooks/useRole";
import { useEntitlements } from "../../entitlements/EntitlementsContext";
import { CAP } from "../../entitlements/capabilities";
import useBoosterCheckout from "../../billing/useBoosterCheckout";
import CrmButton from "../ui/CrmButton";
import CrmAlertDialog from "../ui/CrmAlertDialog";
import { CRM } from "../../../constants/testIds/crm";

// Lazy, like the dashboard's charts (SalesChart.jsx): PromoteModal's own tree
// (useCrmMoney, CrmDialog, the three step components) is real weight that a page which
// mounts PromoteButton but never opens it (the overwhelming majority of renders) has no
// reason to pay for — and, concretely, no reason to require every EXISTING test of
// CourseEditor/ProductsAdmin/PublicProfileAdmin to newly account for.
const PromoteModal = lazy(() => import("./PromoteModal"));

const RUNNING_STATUSES = new Set(["pending_payment", "active"]);

/**
 * `<PromoteButton itemType="course|product|profile" itemId itemName thumbnailUrl />`
 * (crm-frontend-design.md §B.4, §E.9). Self-contained: it owns the modal, the "is a run
 * already going" read, and the round trip through the marketplace-promotion booster's
 * checkout — the three callers (`CourseEditor`, `ProductsAdmin`, `PublicProfileAdmin`)
 * pass only what identifies their own item.
 *
 * `null` when `!can("promoteBilling")` (helper; role matrix §E.11) or the item is not
 * saved yet (`itemId` falsy for course/product — a `profile` always has one, the
 * signed-in tenant itself).
 */
export default function PromoteButton({ itemType, itemId, itemName, thumbnailUrl }) {
  const t = useT("crm");
  const tc = useT("common");
  const { can } = useRole();
  const ent = useEntitlements();
  const [running, setRunning] = useState(null); // the active/pending_payment row, or null
  const [loaded, setLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(0);
  const [stopping, setStopping] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    if (!itemId && itemType !== "profile") return;
    crmApi.listPromotions({ item_type: itemType, item_id: itemId })
      .then(({ rows }) => {
        const row = (rows || []).find((p) =>
          p.item?.type === itemType && (itemType === "profile" || p.item?.id === itemId) && RUNNING_STATUSES.has(p.status));
        setRunning(row || null);
      })
      .catch(() => setRunning(null))
      .finally(() => setLoaded(true));
  }, [itemType, itemId]);

  useEffect(() => { reload(); }, [reload]);

  // Booster return: `?promote=<type>:<id>&promote_step=2` (§B.4) names THIS button's own
  // item — reopen there. A return that names a different item (another tab, another
  // editor) is left alone; only the matching button acts.
  const handleBoosterReturn = useCallback((data) => {
    ent.reload();
    if (data.status === "blocked") toast.error(t("promote.boosterBlocked"));
    reload();
  }, [ent, t, reload]);
  const { purchase: purchaseBooster, busy: checkoutBusy } = useBoosterCheckout({ onReturn: handleBoosterReturn });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const marker = params.get("promote");
    if (marker !== `${itemType}:${itemId}`) return;
    setModalOpen(true);
    setModalStep(params.get("promote_step") === "2" ? 1 : 0);
    const url = new URL(window.location.href);
    url.searchParams.delete("promote");
    url.searchParams.delete("promote_step");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    // Once per mount, from the url this button was mounted with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!can("promoteBilling")) return null;
  if (itemType !== "profile" && !itemId) return null;
  if (!loaded) return null;

  const stop = async () => {
    setBusy(true);
    try {
      await crmApi.stopPromotion(running.id);
      toast.success(t("perf.promoStopped"));
      setStopping(false);
      reload();
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  if (running) {
    return (
      <div className="flex items-center gap-2" data-testid={CRM.promoteRunning}>
        <span className="text-xs font-medium text-eh-ink">{t("promote.runningLabel")}</span>
        <CrmButton variant="link" size="sm" onClick={() => setStopping(true)} data-testid={CRM.promoteStop}>
          {t("promote.stopCta")}
        </CrmButton>
        <CrmAlertDialog
          open={stopping}
          onOpenChange={setStopping}
          title={t("perf.stopTitle")}
          description={t("perf.stopBody", { item: running.item?.name || itemName || "" })}
          confirmLabel={t("perf.stopConfirm")}
          tone="danger"
          busy={busy}
          onConfirm={stop}
          testId={CRM.promoteStopDialog}
          confirmTestId={CRM.promoteStopConfirm}
        />
      </div>
    );
  }

  return (
    <>
      <CrmButton variant="outline" size="sm" onClick={() => { setModalStep(0); setModalOpen(true); }} data-testid={CRM.promote}>
        {t("promote.cta")}
      </CrmButton>
      {modalOpen && (
        <Suspense fallback={null}>
          <PromoteModal
            itemType={itemType}
            itemId={itemId}
            itemName={itemName}
            thumbnailUrl={thumbnailUrl}
            initialStep={modalStep}
            purchaseBooster={purchaseBooster}
            boosterCheckoutBusy={!!checkoutBusy}
            onClose={() => setModalOpen(false)}
            onStarted={() => { setModalOpen(false); reload(); }}
          />
        </Suspense>
      )}
    </>
  );
}
